import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertTriangle, Download } from 'lucide-react';
import Papa from 'papaparse';

const CSVCleaner = () => {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{
    totalRows: number;
    removedRows: number;
    cleanedRows: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const TARGET_DATE = "2022-06-29 22:46:53 UTC";

  const cleanAndDownloadCSV = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setStats(null);

    try {
      const fileContent = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result as string);
        reader.readAsText(file);
      });

      // Parse the CSV
      const parsedData = Papa.parse(fileContent, {
        header: true,
        skipEmptyLines: true
      });

      // Filter out rows with target date
      const cleanedData = parsedData.data.filter((row: any) => 
        row.referred_at !== TARGET_DATE
      );

      // Add cleaning metadata
      const cleaningDate = new Date().toISOString();
      const cleanedDataWithMetadata = cleanedData.map((row: any) => ({
        ...row,
        cleaned_at: cleaningDate
      }));

      // Update stats
      setStats({
        totalRows: parsedData.data.length,
        removedRows: parsedData.data.length - cleanedData.length,
        cleanedRows: cleanedData.length
      });

      // Convert back to CSV
      const cleanedCSV = Papa.unparse(cleanedDataWithMetadata, {
        header: true
      });

      // Create and download file
      const blob = new Blob([cleanedCSV], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const cleanedFilename = file.name.replace('.csv', '_cleaned.csv');
      
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', cleanedFilename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (err) {
      setError('Error processing file: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Limpador de CSV</CardTitle>
          <CardDescription>
            Remove registros com data específica e adiciona metadata de limpeza
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Upload area */}
            <div className="flex justify-center items-center w-full">
              <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <Upload className="w-8 h-8 mb-4 text-gray-500" />
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold">Clique para fazer upload</span> ou arraste e solte
                  </p>
                  <p className="text-xs text-gray-500">CSV (max. 200MB)</p>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) cleanAndDownloadCSV(file);
                  }}
                />
              </label>
            </div>

            {/* Loading indicator */}
            {loading && (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            )}

            {/* Error display */}
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Stats display */}
            {stats && (
              <div className="grid gap-4 grid-cols-3">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Total de Registros</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {stats.totalRows}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-red-600">Registros Removidos</p>
                  <p className="text-2xl font-bold text-red-700">
                    {stats.removedRows}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Registros Limpos</p>
                  <p className="text-2xl font-bold text-green-700">
                    {stats.cleanedRows}
                  </p>
                </div>
              </div>
            )}

            {/* Success message */}
            {stats && !loading && !error && (
              <Alert>
                <Download className="h-4 w-4" />
                <AlertDescription>
                  CSV limpo foi baixado automaticamente.
                  Total de {stats.removedRows} registros removidos.
                </AlertDescription>
              </Alert>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CSVCleaner;